import axios from 'axios';

class CloudflareStreamService {
  constructor() {
    // Use the Cloudflare Worker backend instead of direct API calls
    this.baseURL = process.env.REACT_APP_WORKER_URL || 'http://localhost:8787';
    this.isDevelopmentMode = process.env.REACT_APP_DEVELOPMENT_MODE === 'true' || process.env.NODE_ENV === 'development';
    
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 5000, // 5 second timeout
    });
  }

  // Stream management methods using the backend API
  async getStreams() {
    try {
      const response = await this.client.get('/api/streams');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching streams:', error);
      
      // In development mode, provide mock data if backend is unavailable
      if (this.isDevelopmentMode && (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK')) {
        console.warn('Backend unavailable, using mock streams for development');
        return this.getMockStreams();
      }
      
      return this.getMockStreams();
    }
  }

  async createStream(streamData) {
    try {
      const response = await this.client.post('/api/streams', streamData);
      return response.data;
    } catch (error) {
      console.error('Error creating stream:', error);
      
      // In development mode, provide mock stream creation if backend is unavailable
      if (this.isDevelopmentMode && (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK')) {
        console.warn('Backend unavailable, using mock stream creation for development');
        return this.createMockStream(streamData);
      }
      
      return this.createMockStream(streamData);
    }
  }

  async getStream(streamId) {
    try {
      const response = await this.client.get(`/api/streams/${streamId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stream:', error);
      return this.getMockStream(streamId);
    }
  }

  async startStream(streamId) {
    try {
      // The backend handles stream lifecycle
      const stream = await this.getStream(streamId);
      return { ...stream, status: 'live', startedAt: new Date().toISOString() };
    } catch (error) {
      console.error('Error starting stream:', error);
      throw error;
    }
  }

  async stopStream(streamId) {
    try {
      const stream = await this.getStream(streamId);
      return { ...stream, status: 'offline', endedAt: new Date().toISOString() };
    } catch (error) {
      console.error('Error stopping stream:', error);
      throw error;
    }
  }

  async deleteStream(streamId) {
    try {
      await this.client.delete(`/api/streams/${streamId}`);
      return true;
    } catch (error) {
      console.error('Error deleting stream:', error);
      return true; // Return true for mock purposes
    }
  }

  async addRestreamTarget(streamId, target) {
    try {
      const response = await this.client.post(`/api/streams/${streamId}/restream`, target);
      return response.data;
    } catch (error) {
      console.error('Error adding restream target:', error);
      // Return mock restream target
      return {
        id: `restream-${Date.now()}`,
        platform: target.platform,
        url: target.url,
        streamKey: target.streamKey,
        enabled: true,
        status: 'connected'
      };
    }
  }

  async getRestreamTargets(streamId) {
    try {
      const response = await this.client.get(`/api/streams/${streamId}/restream`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching restream targets:', error);
      return [];
    }
  }

  async getStreamStats(streamId) {
    try {
      const response = await this.client.get(`/api/streams/${streamId}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching stream stats:', error);
      return this.getMockStats();
    }
  }

  // WebRTC signaling methods
  async sendWebRTCOffer(streamId, offer, targetUserId = null) {
    try {
      const response = await this.client.post('/api/webrtc/offer', {
        streamId,
        offer,
        targetUserId,
        clientId: this.getClientId(),
      });
      return response.data;
    } catch (error) {
      console.error('Error sending WebRTC offer:', error);
      throw error;
    }
  }

  async sendWebRTCAnswer(streamId, answer, targetUserId) {
    try {
      const response = await this.client.post('/api/webrtc/answer', {
        streamId,
        answer,
        targetUserId,
        clientId: this.getClientId(),
      });
      return response.data;
    } catch (error) {
      console.error('Error sending WebRTC answer:', error);
      throw error;
    }
  }

  async sendWebRTCIceCandidate(streamId, candidate, targetUserId = null) {
    try {
      const response = await this.client.post('/api/webrtc/ice-candidate', {
        streamId,
        candidate,
        targetUserId,
        clientId: this.getClientId(),
      });
      return response.data;
    } catch (error) {
      console.error('Error sending ICE candidate:', error);
      throw error;
    }
  }

  // Camera upload method
  async uploadCameraCapture(streamId, blob) {
    try {
      const formData = new FormData();
      formData.append('file', blob, 'camera-capture.webm');
      formData.append('streamId', streamId);

      const response = await this.client.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading camera capture:', error);
      throw error;
    }
  }

  // Utility methods
  getClientId() {
    if (!this.clientId) {
      this.clientId = 'client_' + Math.random().toString(36).substr(2, 9);
    }
    return this.clientId;
  }

  // Mock data methods (for development)
  getMockStreams() {
    return [
      {
        id: 'stream-1',
        name: 'Tech Conference Live',
        status: 'live',
        rtmpUrl: 'rtmps://live.cloudflarestream.com/live/',
        rtmpKey: 'demo-key-1',
        playbackUrl: 'https://customer-xyz.cloudflarestream.com/demo1/manifest/video.m3u8',
        webRtcUrl: 'wss://live.cloudflarestream.com/webrtc/demo1',
        thumbnailUrl: 'https://via.placeholder.com/640x360/0051c3/ffffff?text=Tech+Conference',
        viewers: 247,
        duration: 3840,
        createdAt: new Date(Date.now() - 3840000).toISOString(),
        startedAt: new Date(Date.now() - 3840000).toISOString(),
        meta: {
          description: 'Live tech conference with multiple speakers',
        },
      },
      {
        id: 'stream-2',
        name: 'Gaming Stream',
        status: 'live',
        rtmpUrl: 'rtmps://live.cloudflarestream.com/live/',
        rtmpKey: 'demo-key-2',
        playbackUrl: 'https://customer-xyz.cloudflarestream.com/demo2/manifest/video.m3u8',
        webRtcUrl: 'wss://live.cloudflarestream.com/webrtc/demo2',
        thumbnailUrl: 'https://via.placeholder.com/640x360/f38020/ffffff?text=Gaming+Stream',
        viewers: 89,
        duration: 1200,
        createdAt: new Date(Date.now() - 1200000).toISOString(),
        startedAt: new Date(Date.now() - 1200000).toISOString(),
        meta: {
          description: 'Casual gaming session with viewer interaction',
        },
      }
    ];
  }

  createMockStream(streamData) {
    return {
      id: `stream-${Date.now()}`,
      name: streamData.name || 'New Stream',
      status: 'created',
      rtmpUrl: 'rtmps://live.cloudflarestream.com/live/',
      rtmpKey: `key-${Date.now()}`,
      playbackUrl: `https://customer-xyz.cloudflarestream.com/${Date.now()}/manifest/video.m3u8`,
      webRtcUrl: `wss://live.cloudflarestream.com/webrtc/${Date.now()}`,
      thumbnailUrl: 'https://via.placeholder.com/640x360/6b7280/ffffff?text=New+Stream',
      viewers: 0,
      duration: 0,
      createdAt: new Date().toISOString(),
      meta: streamData.meta || {},
      recording: streamData.recording || { mode: 'automatic' },
    };
  }

  getMockStream(streamId) {
    return {
      id: streamId,
      name: 'Mock Stream',
      status: 'offline',
      rtmpUrl: 'rtmps://live.cloudflarestream.com/live/',
      rtmpKey: 'mock-key',
      playbackUrl: `https://customer-xyz.cloudflarestream.com/${streamId}/manifest/video.m3u8`,
      webRtcUrl: `wss://live.cloudflarestream.com/webrtc/${streamId}`,
      thumbnailUrl: 'https://via.placeholder.com/640x360/6b7280/ffffff?text=Mock+Stream',
      viewers: 0,
      duration: 0,
      createdAt: new Date().toISOString(),
      meta: {},
    };
  }

  getMockStats() {
    return {
      viewers: {
        concurrent: Math.floor(Math.random() * 1000),
        total: Math.floor(Math.random() * 10000),
      },
      bandwidth: {
        current: Math.floor(Math.random() * 100),
        peak: Math.floor(Math.random() * 200),
      },
      quality: {
        resolution: '1080p',
        bitrate: Math.floor(Math.random() * 5000 + 2000),
        fps: 30,
      },
      hosts: Math.floor(Math.random() * 5) + 1,
      uptime: Math.floor(Math.random() * 7200), // Up to 2 hours
    };
  }
}

export const cloudflareStreamService = new CloudflareStreamService(); 